import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import _ from 'lodash';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { dimensionMetricsSelector, globalOrderBySelector } from '@store/trends/base/selectors';
import { setDimensionMetrics } from '@store/trends/base/actions';
import { getSecondaryOnlyMetricByKey, isSecondaryOnlyMetric } from '@utils/metrics/metricHelpers';
import { MetricTagsWrapper, StyledTag, TagWrapper } from './styled';
import MetricTag from './MetricTag';
import { track } from '@src/utils';
import { DIMENSION_METRIC_DRAG_END } from '@src/utils/sensor/track';

export const getSecondaryOnlyCleanedUpMetrics = (metrics: string[]) => {
  return metrics.filter((metric) => {
    const secondaryOnlyMetric = isSecondaryOnlyMetric({ metricKey: metric });
    if (secondaryOnlyMetric) {
      return false;
    } else {
      return true;
    }
  });
};

export const addSecondaryOnlyMetrics = (filteredMetrics: string[]) => {
  const res = [...filteredMetrics];
  filteredMetrics.forEach((metric) => {
    const secondaryOnlyMetric = getSecondaryOnlyMetricByKey({ metricKey: metric });
    if (secondaryOnlyMetric) {
      res.push(secondaryOnlyMetric);
    }
  });
  return Array.from(new Set(res));
};

function MetricTags() {
  const dispatch = useDispatch();
  const primaryMetric = useSelector(selectedMetricSelector);
  const globalOrderByMetric = useSelector(globalOrderBySelector);
  const metrics: any = useSelector(dimensionMetricsSelector);

  const [filteredMetrics, setFilteredMetrics] = React.useState(metrics);
  const sensors = useSensors(useSensor(PointerSensor));

  const onClearAll = () => {
    const filteredMetricsSet = new Set([primaryMetric, globalOrderByMetric]);
    dispatch(setDimensionMetrics(Array.from(filteredMetricsSet)));
  };

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) {
      return;
    }
    const metrics = [...filteredMetrics];
    const sourceId = filteredMetrics.indexOf(active.id);
    const destinationId = metrics.indexOf(over.id);
    metrics.splice(sourceId, 1);
    metrics.splice(destinationId, 0, active.id);
    track({
      event: DIMENSION_METRIC_DRAG_END,
      data: { metric: active.id },
    });
    const filteredSecondaryOnlyMetrics = getSecondaryOnlyCleanedUpMetrics(metrics);
    dispatch(setDimensionMetrics(addSecondaryOnlyMetrics(filteredSecondaryOnlyMetrics)));
  }

  useEffect(() => {
    // skip order by metric tag if it's not the primary metric
    const filteredSecondaryOnlyMetrics = getSecondaryOnlyCleanedUpMetrics(metrics);
    setFilteredMetrics(filteredSecondaryOnlyMetrics);
  }, [metrics]);

  const showClearButton = filteredMetrics.length > 1; // metrics.some(metric => ![primaryMetric, globalOrderByMetric].includes(metric));

  return (
    <MetricTagsWrapper>
      <TagWrapper>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={filteredMetrics} strategy={rectSortingStrategy}>
            {filteredMetrics?.map((metric: string) => (
              <MetricTag key={metric} metric={metric} metrics={filteredMetrics} />
            ))}
          </SortableContext>
        </DndContext>
      </TagWrapper>
      {showClearButton && (
        <StyledTag
          onClick={onClearAll}
          style={{ cursor: 'pointer', margin: '0px 5px' }}
          $disableMode={false}
        >
          Clear All
        </StyledTag>
      )}
    </MetricTagsWrapper>
  );
}

export default MetricTags;
